/**
 * useProfile.ts - React Hook for Profile Management
 * 
 * CHANGE HISTORY:
 * - 251102_1647 danielbeer: Create useProfile hook for React integration
 * 
 * Provides easy access to Profylr in React components
 */

import { useState, useEffect, useCallback } from 'react';
import type { ProfileManager } from '../ProfileManager';
import type { Profile, ResolvedProfile } from '../types';

export interface UseProfileOptions {
  profileManager: ProfileManager;
  autoLoad?: boolean; // Auto-load active profile on mount
}

export interface UseProfileReturn {
  // Current state
  activeProfile: ResolvedProfile | null;
  profiles: Profile[];
  loading: boolean;
  error: string | null;
  
  // Actions
  activate: (profileId: string) => Promise<void>;
  deactivate: () => Promise<void>;
  create: (profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Profile>;
  update: (profileId: string, updates: Partial<Profile>) => Promise<Profile>;
  delete: (profileId: string) => Promise<void>;
  refresh: () => Promise<void>;
  
  // Utilities
  getProfile: (profileId: string) => Promise<Profile | null>;
  listProfiles: () => Promise<Profile[]>;
}

/**
 * React hook for profile management
 * 
 * @example
 * ```tsx
 * const { activeProfile, activate, profiles } = useProfile({
 *   profileManager: profylr
 * });
 * 
 * // Activate a profile
 * await activate('marketing-messaging');
 * 
 * // Current profile
 * console.log(activeProfile?.name);
 * ```
 */
export function useProfile(options: UseProfileOptions): UseProfileReturn {
  const { profileManager, autoLoad = true } = options;
  
  const [activeProfile, setActiveProfile] = useState<ResolvedProfile | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Load initial data
  useEffect(() => {
    if (autoLoad) {
      loadInitialData();
    }
  }, [autoLoad]);
  
  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load active profile
      const active = await profileManager.getActiveProfile();
      setActiveProfile(active);
      
      // Load all profiles
      const allProfiles = await profileManager.list();
      setProfiles(allProfiles);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const activate = useCallback(async (profileId: string) => {
    setLoading(true);
    setError(null);
    try {
      await profileManager.activate(profileId);
      const active = await profileManager.getActiveProfile();
      setActiveProfile(active);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const deactivate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await profileManager.deactivate();
      setActiveProfile(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const create = useCallback(async (profileData: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    setError(null);
    try {
      const profile = await profileManager.create(profileData);
      await refresh();
      return profile;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const update = useCallback(async (profileId: string, updates: Partial<Profile>) => {
    setLoading(true);
    setError(null);
    try {
      const profile = await profileManager.update(profileId, updates);
      await refresh();
      return profile;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const deleteProfile = useCallback(async (profileId: string) => {
    setLoading(true);
    setError(null);
    try {
      await profileManager.delete(profileId);
      await refresh();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const active = await profileManager.getActiveProfile();
      setActiveProfile(active);
      
      const allProfiles = await profileManager.list();
      setProfiles(allProfiles);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileManager]);
  
  const getProfile = useCallback(async (profileId: string) => {
    return await profileManager.read(profileId);
  }, [profileManager]);
  
  const listProfiles = useCallback(async () => {
    return await profileManager.list();
  }, [profileManager]);
  
  return {
    activeProfile,
    profiles,
    loading,
    error,
    
    activate,
    deactivate,
    create,
    update,
    delete: deleteProfile,
    refresh,
    
    getProfile,
    listProfiles
  };
}

export default useProfile;
